/**
 * e-Sign client: types and calls for /api/v1/esign (contract: docs/sprints/ESIGN-API.md v1.5).
 * Kept in the module (like lib/viewer.ts for D4) so the shared endpoints.ts stays small.
 */
import { useQuery } from '@tanstack/react-query';
import { api, baseHeaders, getAccessToken, type Page } from '../../../lib/api';
import { useOrgKey } from '../../../lib/queries';

export type RequestStatus = 'DRAFT' | 'IN_PROGRESS' | 'RETURNED' | 'SEALING' | 'COMPLETED' | 'REJECTED' | 'VOIDED' | 'EXPIRED';
export type Role = 'APPROVER' | 'SIGNER' | 'VIEWER';
export type Mode = 'ALL' | 'ANY';
export type FieldType = 'SIGNATURE' | 'INITIALS' | 'DATE' | 'NAME' | 'TEXT' | 'CHECKBOX';
export type RecipientStatus = 'PENDING' | 'VIEWED' | 'APPROVED' | 'SIGNED' | 'ACKNOWLEDGED' | 'RETURNED' | 'REJECTED' | 'SKIPPED' | 'REASSIGNED';
export type Box = 'action' | 'waiting' | 'returned' | 'sent' | 'completed' | 'all';
export interface Named { id: string; name: string }

export interface Rule { kind: string; text: string }
export interface Pin { page: number; x: number; y: number; text: string }
/** QXM-RT-07: what acting on a step means on the approval block. */
export type Meaning = 'PREPARED' | 'REVIEWED' | 'APPROVED' | 'ACKNOWLEDGED';
export type ResumeMode = 'RESTART' | 'RESUME';
/** QXM-RT-04 */
export type CommentKind = 'RETURN_REASON' | 'REPLY' | 'NOTE' | 'APPROVE_COMMENT' | 'REJECT_REASON';
export interface Comment { id: string; round: number; step: number | null; recipientId: string | null; author: Named & { name: string | null }; parentId: string | null; kind: CommentKind; body: string; pins: Pin[]; createdAt: string; resolvedAt: string | null }
export interface Thread extends Comment { replies: Comment[] }
export interface CommentRound { round: number; threads: Thread[] }

export interface Recipient {
  id: string;
  userId: string | null;
  groupId: string | null;
  position: string | null;
  name: string;
  role: Role;
  signatureRequired: boolean;
  addedByRequester: boolean;
  status: RecipientStatus;
  viewedAt: string | null;
  actedAt: string | null;
  actedBy: Named | null;
  onBehalfOf: Named | null;
  actingNow: Named[];
  reason: string | null;
  comment: string | null;
  pins: Pin[];
  replacedById: string | null;
  /** v1.5: the layer this recipient returned the request to (0 = requester). */
  returnedToStep?: number | null;
  /** v1.5 (RESUME): approval kept from an earlier round. */
  carriedFromRound?: number | null;
}

export interface Step {
  id: string;
  order: number;
  name: string | null;
  mode: Mode;
  source: 'MATRIX' | 'MANUAL';
  mandatory: boolean;
  matrixLevelKey: string | null;
  rule: Rule | null;
  allowExtraApprovers: boolean;
  afterLevelKey: string | null;
  status: 'WAITING' | 'ACTIVE' | 'DONE';
  /** v1.5 */
  meaning?: Meaning;
  recipients: Recipient[];
}

export interface Field {
  id: string;
  recipientId: string;
  type: FieldType;
  page: number;
  x: number;
  y: number;
  w: number;
  h: number;
  required: boolean;
  value: string | null;
  filled: boolean;
  assetId: string | null;
  placedBy: 'REQUESTER' | 'RECIPIENT';
  round: number;
}

export interface Round { no: number; documentVersionId: string; versionNo: number; sha256: string; pageCount: number | null; createdAt: string; note: string | null }

export interface Me {
  recipientIds: string[];
  isRequester: boolean;
  canAct: boolean;
  canApprove: boolean;
  /** v1.4: true only when a signature is needed (signer, or approver with signatureRequired / a required signature box). */
  canSign: boolean;
  canAcknowledge: boolean;
  canReturn: boolean;
  canReject: boolean;
  canPlaceFields: boolean;
  canEdit: boolean;
  canSend: boolean;
  canVoid: boolean;
  canRemind: boolean;
  canCorrect: boolean;
  canReassign: boolean;
  needsConsent: boolean;
  signatureRequired: boolean;
  missing: { fieldId: string; message: string }[];
  /** v1.5 (QXM-RT-04/05) */
  canComment?: boolean;
  canReply?: boolean;
  canResend?: boolean;
  canChooseResume?: boolean;
}

export interface Detail {
  id: string;
  /** C1 (1d): this request's own approval-block placement (null = document / type / organisation). */
  blockPlacement?: 'bottom' | 'top-right' | 'signoff-page' | 'own-fields' | null;
  title: string;
  message: string | null;
  status: RequestStatus;
  routeSource: 'MATRIX' | 'MANUAL';
  matrixId: string | null;
  matrix: { allowExtraLevels: boolean; amountField: string | null; amountRequired: boolean; conditionAttributes: string[]; copiedAtSend: boolean } | null;
  context: Context | null;
  currentStep: number | null;
  dueAt: string | null;
  overdue: boolean;
  expiresAt: string | null;
  reminderDays: number | null;
  requester: Named;
  documentId: string;
  round: number;
  autoSend: boolean;
  createdAt: string;
  sentAt: string | null;
  completedAt: string | null;
  completedVersionId: string | null;
  certificateFileId: string | null;
  sealedSha256: string | null;
  sealLevel: string | null;
  sealError: string | null;
  voidReason: string | null;
  restricted?: boolean;
  steps: Step[];
  rounds: Round[];
  fields: Field[];
  me: Me;
  settings: { recipientsPlaceFields: boolean; signatureMethods: SigMethod[]; savedSignatures: boolean; resumeMode?: ResumeMode; resumeChoice?: boolean; requireReplies?: boolean; approvalBlock?: boolean };
  /** v1.5 (QXM-RT) */
  resumeMode?: ResumeMode;
  resumeFromStep?: number | null;
  returnedToStep?: number | null;
  returnedTo?: { step: number | null; members: Named[] } | null;
  comments?: CommentRound[];
  unansweredReturnIds?: string[];
}

export interface Row {
  id: string;
  title: string;
  status: RequestStatus;
  requester: Named;
  currentStep: number | null;
  stepsTotal: number;
  dueAt: string | null;
  overdue: boolean;
  createdAt: string;
  sentAt: string | null;
  completedAt: string | null;
  myRole: Role | null;
  myStatus: RecipientStatus | null;
  onBehalfOf: Named | null;
  waitingOn: { name: string }[];
  documentId: string;
  round: number;
  autoSend: boolean;
  /** v1.5: returned to a layer I belong to (QXM-RT-08). */
  returnedToStep?: number | null;
  returnedToMe?: boolean;
}

export interface Summary { action: number; waiting: number; returned: number; overdue: number; completedThisWeek: number; sent: number; completed: number }

export interface Context { amount?: number | null; department?: string | null; branch?: string | null; fields?: Record<string, unknown> }

export type SigMethod = 'DRAW' | 'TYPE' | 'UPLOAD' | 'SAVED';
export interface SignatureIn { method: SigMethod; png?: string; svg?: string | null; text?: string | null; savedId?: string }

export interface EsignMe {
  consentAt: string | null;
  savedSignatures: { id: string; kind: 'SIGNATURE' | 'INITIALS'; method: string; createdAt: string }[];
  canSend: boolean;
  settings: { enabled: boolean; recipientsPlaceFields: boolean; signatureMethods: SigMethod[]; savedSignatures: boolean; webRecheck: 'PASSWORD_OR_TOTP' | 'TOTP_ONLY'; mobileRecheck: string; mobileCompression: string; allowManualRoutes: boolean; resumeMode?: ResumeMode; resumeChoice?: boolean; requireReplies?: boolean; approvalBlock?: boolean };
}

export interface TimelineEvent {
  seq: number;
  at: string;
  type: string;
  round: number;
  actor: Named | null;
  onBehalfOf: Named | null;
  recipientId: string | null;
  data: Record<string, unknown> | null;
  ip: string | null;
  device: string | null;
  recheck: { method: string; deviceId?: string } | null;
  hash: string;
  prevHash: string | null;
}

/* ─── Matrix ─────────────────────────────────────────────────────────────── */

export type CondOp = '=' | '!=' | '>' | '>=' | '<' | '<=' | 'in' | 'notIn' | 'contains' | 'exists';
export type Condition = { all: Condition[] } | { any: Condition[] } | { not: Condition } | { attr: string; op: CondOp; value?: unknown };
export type Approver = { userId: string } | { groupId: string } | { position: string };
export interface LevelField { type: FieldType; page: number; x: number; y: number; w: number; h: number; required?: boolean }
export interface MatrixLevel {
  key: string;
  name?: string | null;
  mode: Mode;
  mandatory: boolean;
  role: 'APPROVER' | 'SIGNER';
  signatureRequired: boolean;
  allowExtraApprovers: boolean;
  approvers: Approver[];
  when?: Condition | null;
  skipWhen?: Condition | null;
  fields?: LevelField[];
  /** QXM-RT-07; null = default rule (last level approves, the others review). */
  meaning?: Meaning | null;
}
export interface MatrixDefinition { amountField?: string | null; allowExtraLevels: boolean; levels: MatrixLevel[] }
export interface Matrix {
  id: string;
  documentTypeId: string;
  department: string | null;
  branch: string | null;
  name: string;
  active: boolean;
  current: { id: string; versionNo: number; effectiveFrom: string; definition: MatrixDefinition } | null;
  scheduled: { id: string; versionNo: number; effectiveFrom: string }[];
  versions: { id: string; versionNo: number; effectiveFrom: string; note: string | null; createdById: string | null; createdAt: string }[];
}

export interface PreviewRecipient { userId: string | null; groupId: string | null; position: string | null; role: Role; signatureRequired: boolean; addedByRequester: boolean; name: string; resolvesTo?: string[] }
export interface PreviewStep { order: number; name: string | null; mode: Mode; source: 'MATRIX' | 'MANUAL'; mandatory: boolean; matrixLevelKey: string | null; rule: Rule | null; afterLevelKey: string | null; allowExtraApprovers: boolean; recipients: PreviewRecipient[] }
export interface Preview {
  matrix: { matrixId: string; versionNo: number } | null;
  allowExtraLevels: boolean;
  amountField: string | null;
  amountRequired: boolean;
  amountMissing: boolean;
  conditionAttributes: string[];
  steps: PreviewStep[];
  skipped: { key: string; reason: string }[];
  problems: string[];
}

export interface Delegation {
  id: string;
  userId: string;
  substituteId: string;
  userName: string | null;
  substituteName: string | null;
  documentTypeId: string | null;
  fromAt: string;
  toAt: string;
  reason: string | null;
  revokedAt: string | null;
  active: boolean;
}

/* ─── Calls ─────────────────────────────────────────────────────────────── */

const R = (id: string) => `/esign/requests/${encodeURIComponent(id)}`;
export const esign = {
  summary: () => api<Summary>('/esign/summary', { background: true }),
  list: (q: Record<string, string | number | undefined>) => api<Page<Row>>('/esign/requests', { query: q }),
  detail: (id: string) => api<Detail>(R(id)),
  create: (body: Record<string, unknown>, key?: string) => api<Detail>('/esign/requests', { method: 'POST', body, idempotencyKey: key }),
  patch: (id: string, body: Record<string, unknown>) => api<Detail>(R(id), { method: 'PATCH', body }),
  discard: (id: string) => api<{ ok: true }>(R(id), { method: 'DELETE' }),
  route: (id: string, steps: unknown[]) => api<Detail>(`${R(id)}/route`, { method: 'PUT', body: { steps } }),
  setFields: (id: string, fields: unknown[]) => api<Field[]>(`${R(id)}/fields`, { method: 'PUT', body: { fields } }),
  send: (id: string) => api<Detail>(`${R(id)}/send`, { method: 'POST', query: { wait: true }, body: {} }),
  view: (id: string) => api<{ ok: true }>(`${R(id)}/view`, { method: 'POST', body: { device: 'web' } }),
  approve: (id: string, body: Record<string, unknown>, key: string) => api<Detail>(`${R(id)}/approve`, { method: 'POST', body: { ...body, device: 'web' }, idempotencyKey: key }),
  sign: (id: string, body: Record<string, unknown>, key: string) => api<Detail>(`${R(id)}/sign`, { method: 'POST', body: { ...body, device: 'web' }, idempotencyKey: key }),
  acknowledge: (id: string) => api<Detail>(`${R(id)}/acknowledge`, { method: 'POST', body: { device: 'web' } }),
  giveBack: (id: string, reason: string, pins: Pin[], toStep = 0) => api<Detail>(`${R(id)}/return`, { method: 'POST', body: { reason, pins, toStep, device: 'web' } }),
  comments: (id: string) => api<{ rounds: CommentRound[]; unansweredReturnIds: string[] }>(`${R(id)}/comments`),
  addComment: (id: string, body: string, parentId?: string | null) => api<{ rounds: CommentRound[]; unansweredReturnIds: string[] }>(`${R(id)}/comments`, { method: 'POST', body: { body, parentId: parentId ?? null } }),
  reject: (id: string, reason: string) => api<Detail>(`${R(id)}/reject`, { method: 'POST', body: { reason, device: 'web' } }),
  version: (id: string, body: { versionId: string; note?: string | null; resend: boolean; context?: Context | null; resumeMode?: ResumeMode | null }) => api<Detail>(`${R(id)}/versions`, { method: 'POST', body }),
  resend: (id: string, resumeMode?: ResumeMode | null) => api<Detail>(`${R(id)}/resend`, { method: 'POST', body: resumeMode ? { resumeMode } : {} }),
  void: (id: string, reason: string) => api<Detail>(`${R(id)}/void`, { method: 'POST', body: { reason } }),
  remind: (id: string) => api<{ notified: number }>(`${R(id)}/remind`, { method: 'POST', body: {} }),
  reassign: (id: string, recipientId: string, userId: string, reason: string) => api<Detail>(`${R(id)}/recipients/${recipientId}/reassign`, { method: 'POST', body: { userId, reason } }),
  timeline: (id: string) => api<TimelineEvent[]>(`${R(id)}/timeline`),
  pdfUrl: (id: string, round?: number | 'final') => api<{ url: string; sha256?: string; pageCount?: number | null; expiresAt: string }>(`${R(id)}/pdf/url`, { query: { round } }),
  signatureUrl: (id: string, assetId: string) => api<{ url: string }>(`${R(id)}/signatures/${assetId}/url`),
  certificateUrl: (id: string) => api<{ url: string }>(`${R(id)}/certificate/url`),
  recheck: (body: ({ method: 'PASSWORD'; password: string } | { method: 'TOTP'; code: string }) & { requestId?: string }) => api<{ signingToken: string; expiresAt: string; method: string }>('/esign/recheck', { method: 'POST', body }),
  me: () => api<EsignMe>('/esign/me'),
  consent: () => api<{ consentAt: string }>('/esign/consent', { method: 'POST', body: { accept: true } }),
  saveSignature: (kind: 'SIGNATURE' | 'INITIALS', png: string, svg: string | null) => api<{ id: string }>('/esign/saved-signatures', { method: 'POST', body: { kind, png, svg, accept: true } }),
  savedUrl: (id: string) => api<{ url: string }>(`/esign/saved-signatures/${id}/url`),
  deleteSaved: (id: string) => api<{ ok: true }>(`/esign/saved-signatures/${id}`, { method: 'DELETE' }),
  matrices: (documentTypeId?: string) => api<Matrix[]>('/esign/matrices', { query: { documentTypeId } }),
  matrixVersion: (id: string, no: number) => api<{ definition: MatrixDefinition; effectiveFrom: string; versionNo: number; note: string | null }>(`/esign/matrices/${id}/versions/${no}`),
  createMatrix: (body: Record<string, unknown>) => api<Matrix>('/esign/matrices', { method: 'POST', body }),
  addMatrixVersion: (id: string, body: Record<string, unknown>) => api<Matrix>(`/esign/matrices/${id}/versions`, { method: 'POST', body }),
  patchMatrix: (id: string, body: Record<string, unknown>) => api<Matrix>(`/esign/matrices/${id}`, { method: 'PATCH', body }),
  preview: (body: Record<string, unknown>) => api<Preview>('/esign/matrices/preview', { method: 'POST', body }),
  delegations: (all: boolean) => api<Delegation[]>('/esign/delegations', { query: { all: all || undefined } }),
  delegate: (body: Record<string, unknown>) => api<Delegation>('/esign/delegations', { method: 'POST', body }),
  revokeDelegation: (id: string) => api<unknown>(`/esign/delegations/${id}`, { method: 'DELETE' }),
  /** C1 (1d): this request's approval-block placement (null = the document's / type's / organisation's). */
  setBlockPlacement: (id: string, placement: string | null) => api<Detail>(`${R(id)}/block-placement`, { method: 'PUT', body: { placement } }),
  wetInk: (typeId: string, requiresWetInk: boolean, reason: string | null) => api<unknown>(`/esign/document-types/${typeId}/wet-ink`, { method: 'PUT', body: { requiresWetInk, reason } }),
};

/** Bytes of the stamped preview (Bearer auth) as a blob URL; `approvalBlock` adds the page-1 block (QXM-RT-07 "How page 1 will look"). */
export async function previewBlobUrl(id: string, round?: number, approvalBlock = false): Promise<string> {
  const token = getAccessToken();
  const qs = new URLSearchParams({ ...(round ? { round: String(round) } : {}), ...(approvalBlock ? { approvalBlock: '1' } : {}) }).toString();
  const res = await fetch(`/api/v1${R(id)}/preview.pdf${qs ? `?${qs}` : ''}`, { credentials: 'include', headers: { ...baseHeaders(), ...(token ? { authorization: `Bearer ${token}` } : {}) } });
  if (!res.ok) throw new Error(`The preview could not be made (${res.status}).`);
  return URL.createObjectURL(await res.blob());
}

/** Inbox badges and dashboard tiles; refreshed every minute in the background. */
export function useEsignSummary(enabled = true) {
  const org = useOrgKey();
  return useQuery({ queryKey: [org, 'esign', 'summary'], enabled, queryFn: esign.summary, refetchInterval: 60_000, retry: false, staleTime: 20_000 });
}

export function useEsignMe() {
  const org = useOrgKey();
  return useQuery({ queryKey: [org, 'esign', 'me'], queryFn: esign.me, staleTime: 60_000, retry: false });
}

/* ─── Labels ─────────────────────────────────────────────────────────────── */

export const STATUS_LABEL: Record<RequestStatus, { label: string; tone: 'neutral' | 'info' | 'warn' | 'ok' | 'danger' | 'violet'; glyph: 'dot' | 'ring' | 'half' | 'bar' | 'cross' | 'square' | 'diamond' }> = {
  DRAFT: { label: 'Draft', tone: 'neutral', glyph: 'ring' },
  IN_PROGRESS: { label: 'In progress', tone: 'info', glyph: 'half' },
  RETURNED: { label: 'Returned for correction', tone: 'warn', glyph: 'bar' },
  SEALING: { label: 'Sealing', tone: 'violet', glyph: 'half' },
  COMPLETED: { label: 'Completed', tone: 'ok', glyph: 'dot' },
  REJECTED: { label: 'Rejected', tone: 'danger', glyph: 'cross' },
  VOIDED: { label: 'Voided', tone: 'neutral', glyph: 'cross' },
  EXPIRED: { label: 'Expired', tone: 'neutral', glyph: 'square' },
};

export const RECIPIENT_LABEL: Record<RecipientStatus, string> = {
  PENDING: 'Waiting',
  VIEWED: 'Viewed',
  APPROVED: 'Approved',
  SIGNED: 'Signed',
  ACKNOWLEDGED: 'Acknowledged',
  RETURNED: 'Returned',
  REJECTED: 'Rejected',
  SKIPPED: 'Not needed',
  REASSIGNED: 'Reassigned',
};

export const ROLE_LABEL: Record<Role, string> = { APPROVER: 'Approver', SIGNER: 'Signer', VIEWER: 'Viewer (copy)' };

/** QXM-RT-07: row labels of the approval block. */
export const MEANING_LABEL: Record<Meaning, string> = { PREPARED: 'Prepared by', REVIEWED: 'Reviewed by', APPROVED: 'Approved by', ACKNOWLEDGED: 'Acknowledged by' };
export const COMMENT_KIND_LABEL: Record<CommentKind, string> = { RETURN_REASON: 'Returned', REPLY: 'Reply', NOTE: 'Note', APPROVE_COMMENT: 'Approve comment', REJECT_REASON: 'Rejected' };

export const FIELD_LABEL: Record<FieldType, string> = { SIGNATURE: 'Signature', INITIALS: 'Initials', DATE: 'Date signed', NAME: 'Name', TEXT: 'Text', CHECKBOX: 'Checkbox' };

/** Default box size in PDF points per field type (signature ≥ 120×36 suggested by the contract). */
export const FIELD_SIZE: Record<FieldType, { w: number; h: number }> = {
  SIGNATURE: { w: 240, h: 72 },
  INITIALS: { w: 64, h: 36 },
  DATE: { w: 100, h: 18 },
  NAME: { w: 160, h: 18 },
  TEXT: { w: 160, h: 20 },
  CHECKBOX: { w: 16, h: 16 },
};

/** The consent sentence (PRD FR-SIG-05; "I agree to sign electronically", once per user). */
export const CONSENT_TEXT =
  'I agree to sign electronically. My electronic signature on this document has the same effect as my handwritten signature (RA 8792, Sec. 8), and the system records how and when I signed.';

/** Recipient colour (chart series tokens, fixed order; light/dark handled in styles.css). */
export const recipientColor = (i: number): string => `var(--series-${(i % 8) + 1})`;
/** The same colour, see-through (field fill). */
export const recipientTint = (i: number, pct = 14): string => `color-mix(in srgb, var(--series-${(i % 8) + 1}) ${pct}%, transparent)`;
